/**
 * A/B Tank Separation Engine
 * 
 * Implements professional calcium-phosphate separation logic
 * Based on HydroBuddy Pascal A+B solution algorithms
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import Grid from '../common/Grid';
import {
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { Button } from '@mui/material';
import { ECCalculationService } from '../../services/ECCalculationService';
import { partitionSaltsByTank } from '../../utils/tankLogistics';

interface Salt {
  id: string;
  name: string;
  formula: string;
  elements: { symbol: string; percentage: number }[];
  amount: number;
  unit: string;
  purity?: number; // Added for EC calculations
}

interface ABSeparationResult {
  tankA: {
    salts: Salt[];
    totalVolume: number;
    estimatedEC: number;
    estimatedPH: number;
    warnings: string[];
  };
  tankB: {
    salts: Salt[];
    totalVolume: number;
    estimatedEC: number;
    estimatedPH: number;
    warnings: string[];
  };
  incompatibleSalts: string[];
  separationEfficiency: number;
}

interface ABTankSeparationEngineProps {
  salts: Salt[];
  stockVolume: number;
  onSeparationComplete: (result: ABSeparationResult) => void;
}

const ABTankSeparationEngine: React.FC<ABTankSeparationEngineProps> = ({
  salts,
  stockVolume,
  onSeparationComplete
}) => {
  const [separationResult, setSeparationResult] = React.useState<ABSeparationResult | null>(null);

  React.useEffect(() => {
    if (salts && salts.length > 0) {
      const result = performABSeparation(salts, stockVolume);
      setSeparationResult(result);
      // Don't auto-complete - let user review and proceed manually
    }
  }, [salts, stockVolume]);

  /**
   * A/B split — same rules as Configuration (tankLogistics / partitionSaltsByTank).
   * Logistics only; grams already computed by matrix solve.
   */
  const performABSeparation = (inputSalts: Salt[], volume: number): ABSeparationResult => {
    const { tankA, tankB, issues } = partitionSaltsByTank(inputSalts);

    // Salts that still mix Ca+P in one product name (edge case)
    const incompatibleSalts = issues
      .filter((i) => i.severity === 'error')
      .map((i) => `${i.saltA} / ${i.saltB}`);

    const tankAProperties = calculateTankProperties(tankA, volume);
    const tankBProperties = calculateTankProperties(tankB, volume);
    const efficiency = calculateSeparationEfficiency(tankA, tankB, incompatibleSalts);

    // Tag tank assignment for report
    tankA.forEach((s) => {
      (s as any).tankAssignment = 'A';
    });
    tankB.forEach((s) => {
      (s as any).tankAssignment = 'B';
    });

    return {
      tankA: {
        salts: tankA,
        totalVolume: volume,
        estimatedEC: tankAProperties.ec,
        estimatedPH: tankAProperties.ph,
        warnings: tankAProperties.warnings,
      },
      tankB: {
        salts: tankB,
        totalVolume: volume,
        estimatedEC: tankBProperties.ec,
        estimatedPH: tankBProperties.ph,
        warnings: tankBProperties.warnings,
      },
      incompatibleSalts,
      separationEfficiency: efficiency,
    };
  };

  /**
   * Calculate tank properties (EC, pH, warnings)
   * Implements HydroBuddy Pascal LMCv2 EC model for A/B tanks
   */
  const calculateTankProperties = (tankSalts: Salt[], volume: number) => {
    let totalSolids = 0;
    let estimatedPH = 6.5; // Default neutral pH
    const warnings: string[] = [];

    // HydroBuddy EC contribution coefficients (LMCv2 model)
    const ecContribution = [
      71.46,  // N
      73.5,   // P
      57,     // K
      73,     // Mg
      106,    // Ca
      119,    // S
      160,    // B
      108.0,  // Fe
      0,      // Mn
      0,      // Zn
      0,      // Cu
      0,      // Mo
      0,      // Si (does not conduct at hydroponic pH)
      76.35,  // Cl
      50.01,  // Na
      0       // Reserved
    ];

    // Element molar masses (g/mol)
    const molarMass = [
      14.007,  // N
      30.974,  // P
      39.098,  // K
      24.305,  // Mg
      40.078,  // Ca
      32.065,  // S
      10.811,  // B
      55.845,  // Fe
      54.938,  // Mn
      65.38,   // Zn
      63.546,  // Cu
      95.96,   // Mo
      28.085,  // Si
      35.453,  // Cl
      22.990,  // Na
      1.0      // Reserved
    ];

    // Element charges for ionic strength calculation
    const zi = [1, 3, 1, 2, 2, 2, 3, 3, 2, 2, 2, 6, 4, 1, 1, 1];

    // Element symbols for ECCalculationService
    const elementSymbols = ['N', 'P', 'K', 'Mg', 'Ca', 'S', 'B', 'Fe', 'Mn', 'Zn', 'Cu', 'Mo', 'Si', 'Cl', 'Na', 'Reserved'];

    // Calculate element concentrations for this tank
    const elementConcentrations = new Array(16).fill(0);

    tankSalts.forEach(salt => {
      totalSolids += salt.amount;

      // Calculate element contributions from this salt using EXACT Pascal formula
      if (salt.elements && Array.isArray(salt.elements)) {
        salt.elements.forEach((element, idx) => {
          // CRITICAL FIX: The element symbol is nested in element.element.symbol
          const elementSymbol = (element as any).element?.symbol || element.symbol;
          const elementIndex = getElementIndex(elementSymbol);

          if (elementIndex >= 0 && elementIndex < 16) {
            // CRITICAL FIX: Use proper Pascal formula for concentration calculation
            // Pascal: concentration = (salt_amount * percentage * purity) / (100 * volume)
            const purity = (salt as any).purity || 100;
            const concentrationPpm = (salt.amount * element.percentage * purity) / (100 * volume);
            elementConcentrations[elementIndex] += concentrationPpm;
          }
        });
      }

      // pH estimation based on salt type
      if (salt.formula.includes('NO3')) {
        estimatedPH += 0.05; // Nitrates slightly increase pH
      } else if (salt.formula.includes('H2PO4')) {
        estimatedPH -= 0.15; // Phosphates decrease pH more significantly
      } else if (salt.formula.includes('SO4')) {
        estimatedPH -= 0.05; // Sulfates slightly acidic
      }
    });

    // Calculate ionic strength for LMCv2 model
    let ionicStrength = 0;
    for (let i = 0; i < 16; i++) {
      const conc = elementConcentrations[i]; // ppm
      if (conc > 0) {
        const molarity = conc / (1000 * molarMass[i]); // Convert ppm to molarity
        ionicStrength += zi[i] * zi[i] * molarity;
      }
    }

    // Calculate EC using centralized ECCalculationService for accurate Pascal HydroBuddy algorithms
    const elementConcentrationsForEC = elementConcentrations.map((conc, i) => ({
      symbol: elementSymbols[i],
      concentration: conc
    })).filter(item => item.concentration > 0);

    const ecResult = ECCalculationService.calculate(elementConcentrationsForEC, 'LMCv2');
    const stockEC = ecResult.predicted;

    // Pascal result is already in mS/cm
    const estimatedEC = stockEC;

    // A/B tank EC calculation completed successfully

    // Check for warnings
    if (totalSolids > volume * 200) { // 200g/L threshold for stock solutions
      warnings.push('High solids concentration - check solubility limits');
    }

    if (estimatedEC > 5.0) {
      warnings.push('High EC - may cause nutrient burn when diluted');
    }

    // Clamp pH to reasonable range
    estimatedPH = Math.max(4.0, Math.min(8.0, estimatedPH));

    return {
      ec: Math.round(estimatedEC * 1000) / 1000, // Round to 3 decimal places like HydroBuddy
      ph: Math.round(estimatedPH * 10) / 10,
      warnings
    };
  };

  // Helper function to get element index
  const getElementIndex = (symbol: string): number => {
    const elementMap: { [key: string]: number } = {
      'N': 0, 'P': 1, 'K': 2, 'Mg': 3, 'Ca': 4, 'S': 5,
      'B': 6, 'Fe': 7, 'Mn': 8, 'Zn': 9, 'Cu': 10, 'Mo': 11,
      'Si': 12, 'Cl': 13, 'Na': 14
    };
    return elementMap[symbol] ?? -1;
  };

  /**
   * Calculate separation efficiency
   */
  const calculateSeparationEfficiency = (tankA: Salt[], tankB: Salt[], incompatible: string[]): number => {
    const totalSalts = tankA.length + tankB.length;
    const incompatibleCount = incompatible.length;
    
    if (totalSalts === 0) return 0;
    
    // Efficiency based on successful separation and balance
    const balanceScore = 1 - Math.abs(tankA.length - tankB.length) / totalSalts;
    const compatibilityScore = 1 - incompatibleCount / totalSalts;
    
    return Math.round((balanceScore * 0.4 + compatibilityScore * 0.6) * 100);
  };

  if (!separationResult) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            A/B Tank Separation Analysis
          </Typography>
          <Typography color="text.secondary">
            Analyzing salt compatibility for A/B tank separation...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            A/B Tank Separation Results
          </Typography>
          
          {/* Separation Efficiency */}
          <Alert 
            severity={separationResult.separationEfficiency > 80 ? 'success' : 
                     separationResult.separationEfficiency > 60 ? 'warning' : 'error'}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              <strong>Separation Efficiency: {separationResult.separationEfficiency}%</strong>
              {separationResult.incompatibleSalts.length > 0 && (
                <span> - {separationResult.incompatibleSalts.length} incompatible salt(s) detected</span>
              )}
            </Typography>
          </Alert>

          {/* Incompatible Salts Warning */}
          {separationResult.incompatibleSalts.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Incompatible Salts Detected:</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {separationResult.incompatibleSalts.map(salt => (
                  <Chip key={salt} label={salt} color="warning" size="small" />
                ))}
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                These salts contain calcium/magnesium with phosphate - may cause precipitation.
              </Typography>
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Tank A */}
            <Grid xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Tank A (Calcium/Magnesium)
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Volume: {separationResult.tankA.totalVolume}L | 
                    EC: {separationResult.tankA.estimatedEC} mS/cm | 
                    pH: {separationResult.tankA.estimatedPH}
                  </Typography>

                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Salt</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {separationResult.tankA.salts.map(salt => (
                          <TableRow key={salt.id}>
                            <TableCell>{salt.name}</TableCell>
                            <TableCell align="right">{salt.amount.toFixed(2)} {salt.unit}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {separationResult.tankA.warnings.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {separationResult.tankA.warnings.map((warning, index) => (
                        <Typography key={index} variant="body2">• {warning}</Typography>
                      ))}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Tank B */}
            <Grid xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" color="secondary" gutterBottom>
                    Tank B (Phosphate/Sulfate)
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Volume: {separationResult.tankB.totalVolume}L | 
                    EC: {separationResult.tankB.estimatedEC} mS/cm | 
                    pH: {separationResult.tankB.estimatedPH}
                  </Typography>

                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Salt</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {separationResult.tankB.salts.map(salt => (
                          <TableRow key={salt.id}>
                            <TableCell>{salt.name}</TableCell>
                            <TableCell align="right">{salt.amount.toFixed(2)} {salt.unit}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {separationResult.tankB.warnings.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {separationResult.tankB.warnings.map((warning, index) => (
                        <Typography key={index} variant="body2">• {warning}</Typography>
                      ))}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Proceed to pH Adjustment Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                if (separationResult && onSeparationComplete) {
                  onSeparationComplete(separationResult);
                }
              }}
              startIcon={<CheckIcon />}
            >
              Proceed to pH Adjustment
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ABTankSeparationEngine;
